import { Outlet, Link, useNavigate } from "react-router-dom";
import { Boxes, ArrowLeft, ScrollText, Lightbulb, Store, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AgeGate } from "@/components/AgeGate";
import { usePortalSettings } from "@/hooks/usePortalSettings";
import { useAuth } from "@/context/AuthContext";
import { ReleaseBadge } from "@/components/ReleaseBadge";

/**
 * Layout for the Playden 3D Printing side. Deliberately kept lightweight and
 * distinct from the RPG Layout — different accent (fuchsia), different nav.
 * The 21+ age gate only appears if the admin has flipped the portal setting
 * (e.g. an NSFW figurine drop lands in the store).
 */
export function PrintsLayout() {
  const { user, logout } = useAuth();
  const settings = usePortalSettings();
  const gateForced = !!settings?.prints_gate_forced;
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Subtle print-shop ambient — fuchsia + warm neutrals to differentiate from RPG cyan */}
      <div className="absolute inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(1100px_600px_at_80%_10%,rgba(244,114,182,0.10),transparent),radial-gradient(1100px_600px_at_10%_90%,rgba(251,191,36,0.06),transparent)]" />
        <div className="absolute inset-0 bg-[hsl(222_22%_5%)]" style={{ mixBlendMode: "multiply" }} />
      </div>

      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[hsl(var(--background))]/80 border-b border-white/5" data-testid="prints-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-16 py-2 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <Link to="/prints" data-testid="prints-brand-link" className="flex items-center gap-3 group min-w-0 max-w-full">
            <div className="relative h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-black shadow-[0_0_22px_rgba(244,114,182,0.5)] border border-white/15">
              <Boxes className="absolute inset-0 m-auto h-7 w-7 text-fuchsia-300" />
            </div>
            <span className="flex flex-wrap items-center gap-2 min-w-0">
              <span className="font-display text-xl sm:text-2xl tracking-[0.18em] text-white group-hover:text-fuchsia-300 transition-colors">PLAYDEN</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80 border border-fuchsia-300/30 bg-fuchsia-400/10 rounded-full px-2 py-0.5 whitespace-nowrap">3D Printing</span>
              <ReleaseBadge testId="prints-app-version-badge" />
            </span>
          </Link>
          <div data-testid="prints-navigation" className="flex flex-wrap items-center justify-end gap-2 max-w-full ml-auto">
            <Link to="/prints/shop" data-testid="prints-shop-link" className="hidden sm:inline-flex">
              <Button data-testid="prints-shop-button" variant="ghost" size="sm" className="text-white/70 hover:text-fuchsia-300">
                <Store className="h-4 w-4 mr-1.5" /> Shop
              </Button>
            </Link>
            <Link to="/prints/quote" data-testid="prints-quote-link" className="hidden sm:inline-flex">
              <Button data-testid="prints-quote-button" variant="ghost" size="sm" className="text-white/70 hover:text-fuchsia-300">
                <Upload className="h-4 w-4 mr-1.5" /> Quote
              </Button>
            </Link>
            <Link to="/" data-testid="prints-back-to-portal">
              <Button data-testid="prints-portal-button" variant="ghost" size="sm" className="text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Portal
              </Button>
            </Link>
            {user ? (
              <Button variant="ghost" size="sm" onClick={async () => { if (await logout()) navigate("/"); }} data-testid="prints-logout">
                Sign out
              </Button>
            ) : (
              <Link to="/login" data-testid="prints-signin-link">
                <Button data-testid="prints-signin-button" size="sm" className="bg-fuchsia-400 text-black hover:bg-fuchsia-300">Sign in</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Age gate only renders when admin has forced it on the 3D Prints side. */}
      {gateForced && <AgeGate />}

      <footer className="mt-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="font-mono text-xs text-white/40 uppercase tracking-[0.3em]">Playden Studios · 3D Printing</div>
          <div className="flex items-center gap-4">
            <Link to="/terms" data-testid="prints-footer-terms" className="font-mono text-xs text-white/50 hover:text-fuchsia-300 transition-colors inline-flex items-center gap-1.5">
              <ScrollText className="h-3.5 w-3.5" /> Terms
            </Link>
            <Link to="/prints/changelog" data-testid="prints-footer-changelog" className="font-mono text-xs text-white/50 hover:text-cyan-300 transition-colors inline-flex items-center gap-1.5">
              <ScrollText className="h-3.5 w-3.5" /> Patch Notes
            </Link>
            <Link to="/prints/about" data-testid="prints-footer-about" className="font-mono text-xs text-white/50 hover:text-amber-300 transition-colors inline-flex items-center gap-1.5">
              <Lightbulb className="h-3.5 w-3.5" /> About
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
